import { parseScanDate } from '../formatScanDate.ts';
import { isComfortScan } from '../scanInstructionTone.ts';
import { findLatestPlanningScan, scanTakenAt } from '../scanTakenAt.ts';
import type { DmScanSummary } from '../types';
import { CIRCLE_SIZE } from './geometry.ts';

const DAY_MS = 86_400_000;
const MIN_DOMAIN_MS = 45 * DAY_MS;
const DOMAIN_PAD_RATIO = 0.04;
const RANGE_PAD_RATIO = 0.25;

// Fine enough that zooming a cluster walks same-day scans back onto their true
// dates; a coarser floor would leave them permanently nudged apart.
export const MIN_VIEWPORT_MS = 6 * 60 * 60 * 1000;
/** Clear air between two circles once they have been pushed apart. */
export const MARKER_GAP = 4;
export const MIN_MARKER_STEP = CIRCLE_SIZE + MARKER_GAP;
export const MIN_INTERVAL_WIDTH = 56;

export interface ScaleDomain {
    start: number;
    end: number;
}

/** A scan paired with its parsed date, so nothing downstream re-parses or re-sorts. */
export interface TimelineEntry {
    scan: DmScanSummary;
    time: number | null;
}

export interface TimelineScan {
    scan: DmScanSummary;
    time: number;
    /** Paint position, which is the true date position unless decluttering moved it. */
    x: number;
    /** True when spreadScans had to shift this marker off its exact date. */
    displaced: boolean;
    isSelected: boolean;
    isLatest: boolean;
    /** 1-based count of planning scans, for cases with no aligner number; null on comfort. */
    sequence: number | null;
}

export interface MonthBand {
    key: string;
    label: string;
    startX: number;
    endX: number;
    shaded: boolean;
}

export interface TimelineInterval {
    key: string;
    startX: number;
    endX: number;
    days: number;
    /** The scan closing this interval: when it was due, when it landed, how late. */
    dueDate: string | null;
    uploadDate: string | null;
    late: boolean;
    lateDays: number | null;
}

function scanTime(scan: DmScanSummary): number | null {
    return parseScanDate(scanTakenAt(scan))?.getTime() ?? null;
}

/** Parse once, sort once; undated scans keep their order at the front. */
export function orderScans(scans: DmScanSummary[]): TimelineEntry[] {
    return scans
        .map((scan) => ({ scan, time: scanTime(scan) }))
        .sort((a, b) => (a.time ?? 0) - (b.time ?? 0));
}

export function entryTimes(entries: TimelineEntry[]): number[] {
    return entries.flatMap((entry) => (entry.time == null ? [] : [entry.time]));
}

/** Widens a degenerate range (one scan, or every scan on the same day) around its centre. */
function enforceMinWidth({ start, end }: ScaleDomain): ScaleDomain {
    const width = end - start;
    if (width >= MIN_DOMAIN_MS) return { start, end };
    const centre = start + width / 2;
    return { start: centre - MIN_DOMAIN_MS / 2, end: centre + MIN_DOMAIN_MS / 2 };
}

/** The full extent of the timeline: first scan to last, never out to today. */
export function buildDomain(entries: TimelineEntry[], now: number): ScaleDomain {
    const times = entryTimes(entries);
    const start = times.length ? Math.min(...times) : now;
    const end = times.length ? Math.max(...times) : now;

    const bounded = enforceMinWidth({ start, end });
    const pad = (bounded.end - bounded.start) * DOMAIN_PAD_RATIO;
    return { start: bounded.start - pad, end: bounded.end + pad };
}

function span({ start, end }: ScaleDomain): number {
    return end - start;
}

/** Keeps a viewport inside the full range, never narrower nor wider than allowed. */
export function clampViewport(viewport: ScaleDomain, full: ScaleDomain): ScaleDomain {
    const fullSpan = span(full);
    const width = Math.min(Math.max(span(viewport), Math.min(MIN_VIEWPORT_MS, fullSpan)), fullSpan);
    const start = Math.min(Math.max(viewport.start, full.start), full.end - width);
    return { start, end: start + width };
}

/** Moves one edge of the viewport, keeping the other pinned. */
export function resizeViewport(
    viewport: ScaleDomain,
    full: ScaleDomain,
    edge: 'start' | 'end',
    time: number
): ScaleDomain {
    const minSpan = Math.min(MIN_VIEWPORT_MS, span(full));
    if (edge === 'start') {
        return {
            start: Math.min(Math.max(time, full.start), viewport.end - minSpan),
            end: viewport.end
        };
    }
    return {
        start: viewport.start,
        end: Math.max(Math.min(time, full.end), viewport.start + minSpan)
    };
}

/** Recentres the viewport on `time`, keeping its span. */
export function centreViewport(
    viewport: ScaleDomain,
    full: ScaleDomain,
    time: number
): ScaleDomain {
    const half = span(viewport) / 2;
    return clampViewport({ start: time - half, end: time + half }, full);
}

/** Frames an exact date range, padded, never narrower than the minimum span. */
export function rangeViewport(full: ScaleDomain, start: number, end: number): ScaleDomain {
    const centre = (start + end) / 2;
    const width = Math.max((end - start) * (1 + RANGE_PAD_RATIO * 2), MIN_VIEWPORT_MS);
    return clampViewport({ start: centre - width / 2, end: centre + width / 2 }, full);
}

/**
 * Scales the viewport by `factor` about `time`, which stays under the same
 * pixel: the anchor keeps its share of the span rather than being recentred.
 */
export function zoomViewportAt(
    viewport: ScaleDomain,
    full: ScaleDomain,
    time: number,
    factor: number
): ScaleDomain {
    const current = span(viewport);
    const ratio = current > 0 ? (time - viewport.start) / current : 0.5;
    const width = Math.min(
        Math.max(current * factor, Math.min(MIN_VIEWPORT_MS, span(full))),
        span(full)
    );
    const start = time - ratio * width;
    return clampViewport({ start, end: start + width }, full);
}

export function panViewport(
    viewport: ScaleDomain,
    full: ScaleDomain,
    deltaMs: number
): ScaleDomain {
    return clampViewport({ start: viewport.start + deltaMs, end: viewport.end + deltaMs }, full);
}

/** Slides the viewport the shortest distance that brings `time` into view. */
export function ensureVisible(viewport: ScaleDomain, full: ScaleDomain, time: number): ScaleDomain {
    if (inDomain(time, viewport)) return viewport;
    const delta = time < viewport.start ? time - viewport.start : time - viewport.end;
    return panViewport(viewport, full, delta);
}

export function scaleX(time: number, domain: ScaleDomain, width: number): number {
    const span = domain.end - domain.start;
    if (span <= 0) return width / 2;
    return ((time - domain.start) / span) * width;
}

function inDomain(time: number, domain: ScaleDomain): boolean {
    return time >= domain.start && time <= domain.end;
}

// Every in-viewport scan gets a circle at its true date, painted in date order.
export function placeScans(
    entries: TimelineEntry[],
    domain: ScaleDomain,
    width: number,
    selectedScanId: number | null
): TimelineScan[] {
    const dated = entries.filter((entry) => entry.time != null);
    const latestId = findLatestPlanningScan(dated.map((entry) => entry.scan))?.scanId ?? null;

    const placed: TimelineScan[] = [];
    // Counted over every dated scan, not just the in-viewport ones, so a marker
    // keeps the same number as the viewport pans.
    let sequence = 0;
    dated.forEach(({ scan, time }) => {
        const comfort = isComfortScan(scan);
        if (!comfort) sequence++;
        if (time == null || !inDomain(time, domain)) return;
        placed.push({
            scan,
            time,
            x: scaleX(time, domain, width),
            displaced: false,
            isSelected: scan.scanId === selectedScanId,
            isLatest: scan.scanId === latestId,
            sequence: comfort ? null : sequence
        });
    });
    return placed;
}

// Overlapping scans used to collapse into one stacked marker, which hid every
// scan but the representative. They now fan out instead: each keeps its own
// circle, pushed just far enough apart to leave MARKER_GAP px of clear air. The
// cost is a small lie about the exact date of a shifted marker; zooming in
// walks each one back to its true position.
export function spreadScans(
    placed: TimelineScan[],
    width: number,
    minStep: number = MIN_MARKER_STEP
): TimelineScan[] {
    if (placed.length === 0) return [];

    // Input is date-ordered, so only the previous marker can be in the way.
    const xs = placed.map((point) => point.x);
    for (let i = 1; i < xs.length; i++) xs[i] = Math.max(xs[i], xs[i - 1] + minStep);

    // Pushing right can run the tail off the track, so pull the overflow back
    // through the group from the far edge.
    const half = minStep / 2;
    const maxX = Math.max(width - half, half);
    if (xs[xs.length - 1] > maxX) {
        xs[xs.length - 1] = maxX;
        for (let i = xs.length - 2; i >= 0; i--) xs[i] = Math.min(xs[i], xs[i + 1] - minStep);
    }

    return placed.map((point, i) => ({
        ...point,
        x: xs[i],
        displaced: Math.abs(xs[i] - point.x) > 0.5
    }));
}

// Every consecutive pair in view gets a duration label, not just the long ones.
// Comfort scans are off the schedule, so a connector spans straight past them.
export function findIntervals(
    entries: TimelineEntry[],
    domain: ScaleDomain,
    width: number,
    /** Painted x per scan id, so a connector still meets the markers it joins. */
    displacedX?: Map<number, number>
): TimelineInterval[] {
    const dated = entries.filter((entry) => entry.time != null && !isComfortScan(entry.scan));
    const intervals: TimelineInterval[] = [];
    for (let i = 1; i < dated.length; i++) {
        const from = dated[i - 1].time;
        const to = dated[i].time;
        if (from == null || to == null) continue;
        if (to < domain.start || from > domain.end) continue;
        const days = Math.round((to - from) / DAY_MS);
        const closing = dated[i].scan;
        const startX = Math.max(
            0,
            displacedX?.get(dated[i - 1].scan.scanId) ?? scaleX(from, domain, width)
        );
        const endX = Math.min(width, displacedX?.get(closing.scanId) ?? scaleX(to, domain, width));
        intervals.push({
            key: `${dated[i - 1].scan.scanId}-${dated[i].scan.scanId}`,
            startX,
            endX,
            days,
            dueDate: closing.dueDate,
            uploadDate: closing.uploadDate,
            late: closing.late,
            lateDays: closing.daysLate
        });
    }
    return intervals;
}

const MONTH_LABEL = new Intl.DateTimeFormat('en-US', { month: 'short' });

export function buildMonthBands(domain: ScaleDomain, width: number): MonthBand[] {
    if (width <= 0 || domain.end <= domain.start) return [];
    const bands: MonthBand[] = [];
    const cursor = new Date(domain.start);
    cursor.setDate(1);
    cursor.setHours(0, 0, 0, 0);

    let previousYear: number | null = null;
    let index = 0;
    // Guarded so a corrupt domain can never spin here.
    while (cursor.getTime() <= domain.end && index < 600) {
        const next = new Date(cursor);
        next.setMonth(next.getMonth() + 1);
        const year = cursor.getFullYear();
        const showYear = previousYear === null || previousYear !== year;
        bands.push({
            key: `${year}-${cursor.getMonth()}`,
            label: `${MONTH_LABEL.format(cursor).toUpperCase()}${showYear ? ` '${String(year).slice(2)}` : ''}`,
            startX: Math.max(0, scaleX(cursor.getTime(), domain, width)),
            endX: Math.min(width, scaleX(next.getTime(), domain, width)),
            shaded: index % 2 === 1
        });
        previousYear = year;
        cursor.setMonth(cursor.getMonth() + 1);
        index++;
    }
    return bands;
}
